/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import SeamlessImmutable from 'seamless-immutable';
import { pick, isEmpty, once } from 'underscore';
import { createSelector } from 'reselect';
import { PRIMARY_CSS_PATH } from 'DesignData/constants/PrimaryCss';
import { getDenormalizedFields, isFieldCopyable } from 'DesignData/helpers/customWidgetFieldsHelper';
import { getStylesheetsById, getStylesheetsOrderById } from 'DesignData/selectors/stylesheets';
import { getJavascriptsById, getJavascriptsOrderById } from 'DesignData/selectors/javascripts';
import { ANY, EMAIL } from 'DesignData/enums/CustomWidgetHostTemplateTypes';
import { HTML, JS, CSS as PANE_ID_CSS } from 'DesignData/enums/CustomWidgetEditorPaneTypes';
import { SMART_CONTENT } from 'DesignData/constants/Scopes';
import { isScoped as getIsScoped } from 'DesignData/selectors/auth';
import { getIsUngatedForContentTypes } from 'DesignData/selectors/gates';
import { getCustomWidget } from 'DesignData/selectors/customWidgets';
import { getIsInLockedFolderFactory } from 'DesignData/selectors/finder';
import { CriterionTypes, AnonymousCriterionTypes } from 'smart-content-core/constants';
import { ICON } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { STYLE } from 'ContentUtils/constants/CustomWidgetFieldGroupTabTypes';

const getCustomWidgetId = (state, props) => props.assetId || props.customWidgetId;

const getCustomWidgetRevisionId = (state, props) => props.revisionId;

const getById = state => state.customWidgetBuffers.byId;

const getRevisionsById = state => state.customWidgetRevisions.byId;

const getIsSavingById = state => state.customWidgetBuffers.isSavingById;

const getIsValidatingById = state => state.customWidgetBuffers.isValidatingById;

const getSaveErrorById = state => state.customWidgetBuffers.saveErrorById;

const getIsPublishingById = state => state.customWidgetBuffers.isPublishingById;

const getUpgradeErrorById = state => state.customWidgetBuffers.upgradeErrorById;

const getModuleErrorsById = state => state.customWidgetBuffers.moduleErrorsById;

const getFieldsErrorsById = state => state.customWidgetBuffers.fieldsErrorsById;

const getFieldsWarningsById = state => state.customWidgetBuffers.fieldsWarningsById;

const getSourceErrorsById = state => state.customWidgetBuffers.sourceErrorsById;

const getCssErrorsById = state => state.customWidgetBuffers.cssErrorsById;

const getJsErrorsById = state => state.customWidgetBuffers.jsErrorsById;

const getFetchErrorById = state => state.customWidgetBuffers.fetchErrorById;

const EMPTY_OBJECT = SeamlessImmutable({});
const EMPTY_ARRAY = SeamlessImmutable([]);
/**
 * Required Props:
 *  - assetId || customWidgetId
 */

const getCustomWidgetBufferAndFields = (state, props) => {
  const byId = getById(state);
  const customWidgetId = getCustomWidgetId(state, props);
  return byId[customWidgetId] && byId[customWidgetId].present;
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 */


const getPreviousCustomWidgetBufferAndFields = (state, props) => {
  const byId = getById(state);
  const customWidgetId = getCustomWidgetId(state, props);
  const pastData = byId[customWidgetId] && byId[customWidgetId].past;
  return pastData && pastData[pastData.length - 1];
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 * Optional Props:
 *  - revisionId
 */


const getCustomWidgetRevision = (state, props) => {
  const revisionsById = getRevisionsById(state);
  const customWidgetId = getCustomWidgetId(state, props);
  const revisionId = getCustomWidgetRevisionId(state, props);
  return revisionId ? revisionsById[customWidgetId] && revisionsById[customWidgetId].revisions[revisionId] : null;
};
/**
 * HACK: No need to use createSelector since it is just reading from state.
 *       Consequently there is no need for it to be a factory.
 *
 * Required Props:
 *  - assetId || customWidgetId
 * Optional Props:
 *  - revisionId
 */


export const makeGetCustomWidgetBuffer = once(() => {
  return (state, props) => {
    const bufferAndFields = getCustomWidgetBufferAndFields(state, props);
    const revisionId = getCustomWidgetRevisionId(state, props);
    const customWidgetRevision = getCustomWidgetRevision(state, props);
    return revisionId ? customWidgetRevision && customWidgetRevision.buffer || undefined : bufferAndFields && bufferAndFields.buffer || undefined;
  };
});
/**
 * Required props:
 *  - assetId || customWidgetId
 */

export const getCustomWidgetLabel = (state, props) => {
  const customWidgetBuffer = makeGetCustomWidgetBuffer()(state, props);
  return customWidgetBuffer ? customWidgetBuffer.name : '';
};

const getSourceKey = (state, props) => props.sourceKey;
/**
 * Required Props:
 *  - assetId || customWidgetId
 *  - sourceKey
 * Optional Props:
 *  - revisionId
 */


export const getCustomWidgetBufferSource = (state, props) => {
  const customWidgetBuffer = makeGetCustomWidgetBuffer()(state, props);
  const sourceKey = getSourceKey(state, props);
  return customWidgetBuffer && customWidgetBuffer[sourceKey] || '';
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 * Optional Props:
 *  - revisionId
 */

export const getFields = (state, props) => {
  const bufferAndFields = getCustomWidgetBufferAndFields(state, props);
  const revisionId = getCustomWidgetRevisionId(state, props);
  const customWidgetRevision = getCustomWidgetRevision(state, props);
  return revisionId ? customWidgetRevision && customWidgetRevision.fields : bufferAndFields && bufferAndFields.fields;
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 */

const getPreviousFields = (state, props) => {
  const previousBufferAndFields = getPreviousCustomWidgetBufferAndFields(state, props);
  return previousBufferAndFields && previousBufferAndFields.fields;
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 * Optional Props:
 *  - revisionId
 */


export const getCustomWidgetBufferFieldsMap = (state, props) => {
  const fields = getFields(state, props);
  return fields && fields.fields;
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 * Optional Props:
 *  - revisionId
 */

export const makeGetCustomWidgetBufferFields = () => {
  return createSelector([getFields], fields => {
    if (!fields) return EMPTY_ARRAY;
    return getDenormalizedFields(fields);
  });
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 * Optional Props:
 *  - revisionId
 */

export const makeGetCustomWidgetBufferStyleFields = () => {
  const getCustomWidgetBufferFields = makeGetCustomWidgetBufferFields();
  return createSelector([getCustomWidgetBufferFields], fields => {
    const styleField = fields.find(f => {
      return f.tab === STYLE;
    });
    return styleField;
  });
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 * Optional Props:
 *  - revisionId
 */

export const makeGetCustomWidgetBufferContentFields = () => {
  const getCustomWidgetBufferFields = makeGetCustomWidgetBufferFields();
  return createSelector([getCustomWidgetBufferFields], fields => {
    const filteredFields = fields.filter(f => {
      return f.tab !== STYLE;
    });
    return filteredFields;
  });
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 */

export const getPreviousCustomWidgetBufferFields = (state, props) => {
  const previousFields = getPreviousFields(state, props);
  if (!previousFields) return null;
  return previousFields;
};
/**
 * BUG: Will change on any buffer change (code typing).
 * BUG: Uses SeamlessImmutable API.
 * BUG: Returns immutable or non-immutable object depending on buffer state.
 *
 * Required Props:
 *  - assetId || customWidgetId
 * Optional Props:
 *  - revisionId
 */

export const makeGetModuleSpec = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();
  const getCustomWidgetBufferFields = makeGetCustomWidgetBufferFields();
  return createSelector([getCustomWidgetId, getCustomWidgetBuffer, getCustomWidgetBufferFields], (customWidgetId, customWidgetBuffer, customWidgetBufferFields) => {
    return customWidgetBuffer ? customWidgetBuffer.set('fields', customWidgetBufferFields) : {
      id: customWidgetId,
      fields: customWidgetBufferFields
    };
  });
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 */

export const getFieldsErrors = (state, props) => {
  const fieldsErrorsById = getFieldsErrorsById(state, props);
  const customWidgetId = getCustomWidgetId(state, props);
  return fieldsErrorsById[customWidgetId] || EMPTY_OBJECT;
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 *  - field
 */

export const getFieldErrors = (state, props) => {
  const fieldsErrors = getFieldsErrors(state, props);
  const {
    id: fieldId
  } = props.field || {};
  return fieldsErrors[fieldId] || EMPTY_OBJECT;
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 */

export const getFieldsWarnings = (state, props) => {
  const fieldsWarningsById = getFieldsWarningsById(state, props);
  const customWidgetId = getCustomWidgetId(state, props);
  return fieldsWarningsById[customWidgetId] || EMPTY_OBJECT;
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 *  - field
 */

export const getFieldWarnings = (state, props) => {
  const fieldWarnings = getFieldsWarnings(state, props);
  const {
    id: fieldId
  } = props.field || {};
  return fieldWarnings[fieldId] || EMPTY_OBJECT;
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 */

export const getModuleErrors = (state, props) => {
  const moduleErrorsById = getModuleErrorsById(state, props);
  const customWidgetId = getCustomWidgetId(state, props);
  return moduleErrorsById[customWidgetId] || EMPTY_OBJECT;
}; // BUG: The following THREE don't appear to be in use?

export const makeGetSourceErrors = () => {
  return createSelector([getSourceErrorsById, getCustomWidgetId], (sourceErrorsById, customWidgetId) => {
    const sourceErrors = sourceErrorsById[customWidgetId];
    return sourceErrors || EMPTY_ARRAY;
  });
};
export const makeGetCssErrors = () => {
  return createSelector([getCssErrorsById, getCustomWidgetId], (cssErrorsById, customWidgetId) => {
    const cssErrors = cssErrorsById[customWidgetId];
    return cssErrors || EMPTY_ARRAY;
  });
};
export const makeGetJsErrors = () => {
  return createSelector([getJsErrorsById, getCustomWidgetId], (jsErrorsById, customWidgetId) => {
    const jsErrors = jsErrorsById[customWidgetId];
    return jsErrors || EMPTY_ARRAY;
  });
};
export const makeGetRequestStatus = () => {
  let result = EMPTY_OBJECT;
  return createSelector([getCustomWidgetId, getIsSavingById, getSaveErrorById, getIsPublishingById, getUpgradeErrorById, getFetchErrorById, getIsValidatingById], (customWidgetId, isSavingById, saveErrorById, isPublishingById, upgradeErrorById, fetchErrorById, isValidatingById) => {
    result = SeamlessImmutable.merge(result, {
      isValidating: isValidatingById[customWidgetId] || false,
      isSaving: isSavingById[customWidgetId] || false,
      saveError: saveErrorById[customWidgetId],
      isPublishing: isPublishingById[customWidgetId] || false,
      upgradeError: upgradeErrorById[customWidgetId],
      fetchError: fetchErrorById[customWidgetId]
    });
    return result;
  });
};
/**
 * Required Props:
 *  - assetId || customWidgetId
 * Optional Props:
 *  - revisionId
 */

export const makeGetIsReadOnly = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();
  return createSelector([getCustomWidgetBuffer, getCustomWidgetRevisionId, getIsInLockedFolderFactory], (customWidgetBuffer, revisionId, getIsInLockedFolder) => {
    if (!customWidgetBuffer) return true;
    const isViewingRevision = !!revisionId;
    const isCmv1WithOverridableHublTags = customWidgetBuffer.schemaVersion === 1 && customWidgetBuffer.containsOverridableHublTags;
    const isLocked = getIsInLockedFolder(customWidgetBuffer.folderId);
    return !customWidgetBuffer.writeable || isCmv1WithOverridableHublTags || isViewingRevision || isLocked;
  });
};
export const getisUpgradeable = (state, props) => {
  const customWidgetBuffer = makeGetCustomWidgetBuffer()(state, props);
  return customWidgetBuffer && customWidgetBuffer.writeable && customWidgetBuffer.schemaVersion === 1 && !customWidgetBuffer.containsOverridableHublTags;
};
export const makeGetIsEmailOrAnyType = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();
  return createSelector([getCustomWidgetBuffer, getIsUngatedForContentTypes], (customWidgetBuffer, isUngatedForContentTypes) => {
    return isUngatedForContentTypes ? Boolean(customWidgetBuffer && customWidgetBuffer.contentTypes && (~customWidgetBuffer.contentTypes.indexOf(ANY) || ~customWidgetBuffer.contentTypes.indexOf(EMAIL))) : Boolean(customWidgetBuffer && customWidgetBuffer.hostTemplateTypes && (~customWidgetBuffer.hostTemplateTypes.indexOf(ANY) || ~customWidgetBuffer.hostTemplateTypes.indexOf(EMAIL)));
  });
};
export const makeGetIsGlobal = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();
  return createSelector([getCustomWidgetBuffer], customWidgetBuffer => {
    return Boolean(customWidgetBuffer && customWidgetBuffer.global);
  });
};
export const makeGetUpgradeNotPublished = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();
  return createSelector([getCustomWidgetBuffer, getCustomWidget], (customWidgetBuffer, customWidget) => {
    return !!(customWidgetBuffer && customWidgetBuffer.schemaVersion > 1) && !!(customWidget && customWidget.schemaVersion === 1);
  });
};
export const makeGetAbsoluteDataQueryPath = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();
  return createSelector([getCustomWidgetBuffer, getCustomWidget], (customWidgetBuffer, customWidget) => {
    return customWidgetBuffer && customWidgetBuffer.dataQueryAbsolutePath || customWidget && customWidget.dataQueryAbsolutePath;
  });
};
export const makeGetTranslations = () => {
  return createSelector([getCustomWidgetBufferAndFields, getCustomWidgetRevisionId, getCustomWidgetRevision], (bufferAndFields, revisionId, customWidgetRevision) => {
    if (revisionId && customWidgetRevision) {
      return customWidgetRevision && customWidgetRevision.translations;
    }

    return bufferAndFields && bufferAndFields.translations;
  });
};
export const makeGetCustomWidgetBufferPayload = () => {
  const __getCustomWidgetBuffer = makeGetCustomWidgetBuffer();

  const __getCustomWidgetBufferFields = makeGetCustomWidgetBufferFields();

  const __getTranslations = makeGetTranslations();

  return createSelector([__getCustomWidgetBuffer, __getCustomWidgetBufferFields, __getTranslations], (customWidgetBuffer, fields, translations) => {
    const propertiesToSave = pick(customWidgetBuffer, ['name', 'displayName', 'source', 'dataQueryAbsolutePath', 'dataQueryPath', 'otherAssets', 'cssAssets', 'jsAssets', 'css', 'js', 'icon', 'tags', 'contentTags', 'contentTypes', 'hostTemplateTypes', 'isAvailableForNewContent', 'isEnabledForEmailV3Rendering', 'smartType', 'smartObjects', 'definitionId', 'inlineHelpText', 'id', 'path', 'folderId', 'categories']);
    return Object.assign({}, propertiesToSave, {
      fields
    }, translations);
  });
};
export const makeGetCustomWidgetBufferValidationPayload = () => {
  const __getCustomWidgetBuffer = makeGetCustomWidgetBuffer();

  const __getCustomWidgetBufferFields = makeGetCustomWidgetBufferFields();

  return createSelector([__getCustomWidgetBuffer, __getCustomWidgetBufferFields], (customWidgetBuffer, fields) => {
    return {
      source: customWidgetBuffer.source,
      fields
    };
  });
};

function isValidCustomWidgetBuffersState(state) {
  return state && state.buffer && state.fields;
}

export const makeGetPastEditHistoryCount = () => {
  return createSelector([getById, getCustomWidgetId], (byId, customWidgetId) => {
    const past = byId[customWidgetId] && byId[customWidgetId].past;
    return past ? past.filter(isValidCustomWidgetBuffersState).length : 0;
  });
};
export const makeGetFutureEditHistoryCount = () => {
  return createSelector([getById, getCustomWidgetId], (byId, customWidgetId) => {
    const future = byId[customWidgetId] && byId[customWidgetId].future;
    return future ? future.filter(isValidCustomWidgetBuffersState).length : 0;
  });
};
export const makeGetAssets = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();
  const [getOtherAssets, getCssAssets, getJsAssets] = ['otherAssets', 'cssAssets', 'jsAssets'].map(field => {
    return createSelector([getCustomWidgetBuffer], buffer => buffer && buffer[field] || EMPTY_ARRAY);
  });
  return createSelector([getOtherAssets, getCssAssets, getJsAssets], (otherAssets, cssAssets, jsAssets) => {
    return SeamlessImmutable({
      otherAssets,
      cssAssets,
      jsAssets
    });
  });
};
export const makeGetMessages = () => {
  const getTranslations = makeGetTranslations();
  return createSelector([getTranslations], translations => {
    if (!translations) return EMPTY_OBJECT;
    return SeamlessImmutable(translations.messages);
  });
};
export const makeGetMasterLanguage = () => {
  const getTranslations = makeGetTranslations();
  return createSelector([getTranslations], translations => {
    if (!translations) return null;
    return translations.masterLanguage;
  });
};
export const makeGetSourceTranslations = () => {
  const getMessages = makeGetMessages();
  const getMasterLanguage = makeGetMasterLanguage();
  return createSelector([getMessages, getMasterLanguage], (messages, masterLanguage) => {
    if (!messages) return null;
    return messages[masterLanguage] || EMPTY_OBJECT;
  });
};

function getAvailableTemplates(attached, byId, order) {
  const attachedMap = attached ? attached.reduce((map, {
    resource_id: resourceId
  }) => {
    map[resourceId] = resourceId;
    return map;
  }, {}) : {};
  const available = order.reduce((collect, id) => {
    if (attachedMap[id]) {
      return collect;
    }

    const template = byId[id];

    if (template.path === PRIMARY_CSS_PATH) {
      return collect;
    }

    collect.push(template);
    return collect;
  }, []);
  return SeamlessImmutable(available);
}
/**
 * Required Props:
 *  layoutId || assetId
 * Optional Props:
 *  revisionId
 */


export const makeGetAvailableStylesheets = () => {
  const getAssets = makeGetAssets();
  return createSelector([getAssets, getStylesheetsById, getStylesheetsOrderById], (assets, stylesheetsById, stylesheetsOrderById) => {
    return getAvailableTemplates(assets.cssAssets, stylesheetsById, stylesheetsOrderById);
  });
};
/**
 * Required Props:
 *  layoutId || assetId
 * Optional Props:
 *  revisionId
 */

export const makeGetAvailableJavascripts = () => {
  const getAssets = makeGetAssets();
  return createSelector([getAssets, getJavascriptsById, getJavascriptsOrderById], (assets, javascriptsById, javascriptsOrderById) => {
    return getAvailableTemplates(assets.jsAssets, javascriptsById, javascriptsOrderById);
  });
};
/**
 * /path/to/my.module/module.html
 * /path/to/my.module/module.js
 * /path/to/my.module/module.css
 *
 * Required Props:
 *  - assetId || customWidgetId
 *  - paneId
 * Optional Props:
 *  - revisionId
 */

const makeGetModuleSubFilePath = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();

  const getPaneId = (state, props) => props.paneId;

  return createSelector([getCustomWidgetBuffer, getPaneId], (buffer, paneId) => {
    if (!buffer || !buffer.path) return null;
    let {
      path
    } = buffer;

    if (/\.module$/i.test(path) === false) {
      path = `${path}.module`;
    }

    switch (paneId) {
      case HTML:
        return `${path}/module.html`;

      case JS:
        return `${path}/module.js`;

      case PANE_ID_CSS:
        return `${path}/module.css`;

      default:
        return null;
    }
  });
}; // Some fields get hydrated by the renderer with extra properties


const getHydratedDefaultValueForContext = (defaultValue, field) => {
  if (field.occurrence && Array.isArray(defaultValue)) {
    return defaultValue.map(_defaultValue => getHydratedDefaultValueForContext(_defaultValue, field));
  } // Icon fields get hydrated with icon_set developer property


  if (field.type === ICON && defaultValue) {
    return Object.assign({}, defaultValue, {
      icon_set: field.icon_set
    });
  }

  return defaultValue;
};
/*
 * Sets up context data for each CMv2 field for validation
 */


function createFieldGroupContext(field) {
  const fieldContext = field.children.reduce((context, groupField) => {
    if (groupField.type === 'group') {
      context[groupField.name] = createFieldGroupContext(groupField);
    } else if (groupField.default !== undefined) {
      context[groupField.name] = getHydratedDefaultValueForContext(groupField.default, groupField);
    }

    return context;
  }, {});
  return field.occurrence ? [fieldContext] : fieldContext;
}

const emptyFieldsContext = SeamlessImmutable({
  context: {
    module: {}
  },
  modulePath: undefined,
  moduleContentTypes: undefined,
  moduleHostTemplateTypes: undefined
});
export const makeGetFieldsContext = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();
  const getCustomWidgetBufferFields = makeGetCustomWidgetBufferFields();
  const getModuleSubFilePath = makeGetModuleSubFilePath();
  let result = emptyFieldsContext;
  return createSelector([getCustomWidgetBuffer, getCustomWidgetBufferFields, getModuleSubFilePath, getIsUngatedForContentTypes], (customWidgetBuffer, fields, path, isUngatedForContentTypes) => {
    const {
      contentTypes,
      hostTemplateTypes
    } = customWidgetBuffer || {};
    const typesToCheck = isUngatedForContentTypes ? contentTypes : hostTemplateTypes;

    if (!typesToCheck && !path && !fields.length) {
      return result = emptyFieldsContext;
    }

    result = SeamlessImmutable.set(result, 'modulePath', path || undefined);

    if (isUngatedForContentTypes) {
      result = SeamlessImmutable.set(result, 'moduleContentTypes', contentTypes || undefined);
    } else {
      result = SeamlessImmutable.set(result, 'moduleHostTemplateTypes', hostTemplateTypes || undefined);
    }

    const moduleContext = {};
    fields.forEach(field => {
      if (field.type === 'group') {
        const groupContext = createFieldGroupContext(field);
        moduleContext[field.name] = groupContext;
      } else if (field.default !== undefined) {
        const defaultValue = getHydratedDefaultValueForContext(field.default, field);
        moduleContext[field.name] = field.occurrence && isEmpty(field.occurrence) ? [defaultValue] : defaultValue;
      }
    });
    result = SeamlessImmutable.setIn(result, ['context', 'module'], moduleContext);
    return result;
  });
};
export const makeGetCanReplaceNestedModules = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();
  return (state, props) => {
    const customWidgetBuffer = getCustomWidgetBuffer(state, props);
    return customWidgetBuffer && customWidgetBuffer.containsOverridableHublTags && !customWidgetBuffer.default;
  };
};
export const makeGetSmartObjects = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();
  const empty = SeamlessImmutable({});
  let smartObjects = empty;
  return createSelector([getCustomWidgetBuffer], customWidgetBuffer => {
    const {
      smartObjects: _smartObjects
    } = customWidgetBuffer;

    if (!(_smartObjects && _smartObjects.length)) {
      return empty;
    }

    const newSmartObjects = {};

    _smartObjects.forEach(smartObject => {
      const {
        criterion_id: id
      } = smartObject;
      newSmartObjects[id] = smartObject;
    });

    Object.keys(smartObjects).forEach(smartObjectId => {
      if (newSmartObjects[smartObjectId]) {
        return;
      }

      smartObjects = smartObjects.without(smartObjectId);
    });
    smartObjects = smartObjects.merge(newSmartObjects);
    return smartObjects;
  });
};
export const makeGetSmartContentProps = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();
  const defaultState = SeamlessImmutable({
    isEligibleForSmartContent: false,
    anonymousTargeting: false,
    hasSmartContent: false,
    definitionId: null,
    smartType: null,
    smartObjects: []
  });
  let state = defaultState;
  return createSelector([getCustomWidgetBuffer, getIsScoped], (customWidgetBuffer, isScoped) => {
    if (!(customWidgetBuffer && isScoped(SMART_CONTENT))) {
      return defaultState;
    }

    const {
      definitionId = null,
      smartObjects = [],
      smartType
    } = customWidgetBuffer;
    state = state.merge({
      isEligibleForSmartContent: !!customWidgetBuffer.global,
      anonymousTargeting: !!AnonymousCriterionTypes[smartType],
      hasSmartContent: !!(smartType && smartType !== CriterionTypes.NOT_SMART && smartObjects && smartObjects.length),
      definitionId,
      smartType,
      smartObjects
    });
    return state;
  });
};

const getFieldId = (state, props) => props.fieldId;

export const makeGetIsFieldCopyable = () => {
  const getCustomWidgetBufferFields = makeGetCustomWidgetBufferFields();
  let isCopyable = true;
  return createSelector([getCustomWidgetBufferFields, getFieldId], (fields, fieldId) => {
    isCopyable = isFieldCopyable(fields, fieldId);
    return isCopyable;
  });
};
export const makeGetModulePath = () => {
  const getCustomWidgetBuffer = makeGetCustomWidgetBuffer();
  return createSelector([getCustomWidgetBuffer], buffer => {
    if (!buffer || !buffer.path) return null;
    return buffer.path;
  });
};